import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import filter from "lodash/filter";
import MaterialModal from "../MaterialModal";
import h from "../../../helpers";
import { hideModal, lossOfWorkRunQueuedAction } from "../../../actions/actionCreators";
import {
    requestLayoutList,
    setSelectedLayout,
    requestSelectedLayoutObjects,
} from "../../../actions/exportLayoutActions2";
import withModal from "../withModal";
import TextField from "@material-ui/core/TextField";

const LayoutSaveAsModal = ({ isOpen, modalProps }) => {
    const dispatch = useDispatch();

    const [name, setName] = useState("");
    const [errors, setErrors] = useState({});
    const [nameErrors, setNameErrors] = useState("");
    const [hasError, setHasError] = useState(false);

    const layouts = useSelector(state => state.layouts);
    const selectedLayoutObjects = useSelector(state => state.layoutObjects.selectedLayoutObjects || []);

    const afterOpen = () => {
        setName(modalProps.saveAsLayout.LayoutName || "");
        setErrors({});
        setNameErrors("");
        setHasError(false);
    };

    const save = () => {
        if (validate()) {
            // Returns true if there are validation errors
            return;
        }

        let saveAsLayout = modalProps.saveAsLayout;
        saveAsLayout.LayoutName = name;

        fetch("/LayoutsMain/SaveLayout", {
            credentials: "same-origin",
            method: "POST",
            headers: {
                "Content-Type": "application/json; charset=utf-8",
            },
            body: JSON.stringify(saveAsLayout),
        })
            .then(h.checkStatus)
            .then(h.toJson)
            .then(data => {
                if (data.success) {
                    dispatch(requestLayoutList("merge-overwrite", data.layoutId));
                    if (data.layoutId != null) {
                        dispatch(setSelectedLayout(parseInt(data.layoutId)));
                        dispatch(requestSelectedLayoutObjects(parseInt(data.layoutId)));
                        dispatch(lossOfWorkRunQueuedAction());
                    }
                }
                hide();
            })
            .catch(error => {
                h.error("Error saving layout.", error);
                hide();
            });
        hide();
    };

    const validate = () => {
        let myLayouts = filter(layouts);
        const hasMyLayoutName = myLayouts.map(x => x.LayoutName).includes(name);
        const isInExportName =
            selectedLayoutObjects.filter(o => o.ExportName.includes(modalProps.saveAsLayout.LayoutDelimiter)).length >
            0;
        const invalidFormats = validateOutputFormat();

        let nameError = null;
        // Check for blank name
        if (name.trim() == "") {
            nameError = "Name is required";
        }

        // Check for duplicate name on save
        if (hasMyLayoutName) {
            nameError = "This layout name is already used.";
        }

        if (nameError) {
            setNameErrors(nameError);
            setHasError(true);
        }

        // Check if delimiter character is used in any ExportName
        if (isInExportName) {
            errors.delimiter =
                'A column in this layout contains the delimiter. Change the delimiter or click "Cancel" to change the "Export Name" that contains the delimiter.';
        }

        // Check if there are invalid formats
        if (invalidFormats.length > 0) {
            errors.format = "Incorrect format item detected: " + invalidFormats.join(", ");
        }

        setErrors(errors);

        if (Object.keys(errors).length > 0 || nameError) {
            setHasError(true);
            return true;
        } else {
            setHasError(false);
            return false;
        }
    };

    const validateOutputFormat = () => {
        const possibleValues = getPossibleValues();
        let value = modalProps.saveAsLayout.FileNameFormat;

        if (!value) return [];

        let invalidFormats = [];
        let arr = value.split("}");
        arr.forEach(x => {
            if (x.split("{")[1] != null) {
                let formatItem = "{" + x.split("{")[1] + "}";
                if (!possibleValues.includes(formatItem)) {
                    invalidFormats.push(formatItem);
                }
            }
        });
        return invalidFormats;
    };

    const getPossibleValues = () => [
        "{PartnerName}",
        "{CompanyName}",
        "{ID}",
        "{FlowName}",
        "{FlowItemName}",
        "{date:yyyyMMddHHmmss}",
        "{date:yyyyMMdd}",
        "{date:yyyy-MM-dd}",
        "{date:yyyy-MMdd}",
    ];

    const hide = () => dispatch(hideModal("LAYOUT_SAVE_AS"));

    const handleNameChange = val => {
        let nameError = null;
        setName(val);

        let myLayouts = filter(layouts);
        const hasMyLayoutName = myLayouts.map(x => x.LayoutName).includes(val);

        if (val.trim() == "") {
            nameError = "Name is required";
        }

        // Check for duplicate name on save
        if (hasMyLayoutName) {
            nameError = "This layout name is already used.";
        }

        if (nameError) {
            setNameErrors(nameError);
            setHasError(true);
        } else {
            setNameErrors("");
            setHasError(false);
        }
    };

    return (
        <MaterialModal
            id="layout-save-as"
            isOpen={isOpen}
            title="Save Layout As..."
            primaryButtonText="Save"
            afterOpen={afterOpen}
            onClose={hide}
            onPrimaryButtonRequest={save}
            showPrimaryButton={true}
            isPrimaryButtonDisabled={hasError}
            innerDescription={"Make a copy of your layout and carry over any objects and settings"}
        >
            <div style={{ display: "flex", flexDirection: "column" }}>
                <TextField
                    error={hasError}
                    style={{ minWidth: "100px" }}
                    inputProps={{ style: { fontSize: "15px" } }}
                    label="Name"
                    type="text"
                    helperText={nameErrors}
                    value={name}
                    required
                    onChange={e => handleNameChange(e.target.value)}
                />
            </div>
        </MaterialModal>
    );
};

LayoutSaveAsModal.propTypes = {
    modalProps: PropTypes.object,
    isOpen: PropTypes.bool,
    layout: PropTypes.object,
    hideModal: PropTypes.func,
    // layout things
    setSelectedLayout: PropTypes.func,
    layouts: PropTypes.object,
    selectedLayoutObjects: PropTypes.any,
    requestSelectedLayoutObjects: PropTypes.func,
    lossOfWorkRunQueuedAction: PropTypes.func,
    requestLayoutList: PropTypes.func,
};

export default withModal(LayoutSaveAsModal, "LAYOUT_SAVE_AS");
